import { useState } from "react";

import { useUpdateProfileMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { MAX_DISPLAY_NAME_LENGTH } from "@/common/constants";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectMe, setMeProfile } from "@/state/redux/slices/ui";
import { Button } from "../button";
import { Field, FieldError, FieldLabel } from "../field";
import { Input } from "../input";

/**
 * The name shown next to your records and on your space.
 *
 * Deliberately unlike NicknameInput: a display name is not unique and appears
 * in no URL, so it has no availability check, no cooldown and no claim budget.
 * Clearing it falls back to whatever the identity provider supplied — which
 * for a password account is whatever an admin set at creation, or nothing.
 */
export default function DisplayNameInput() {
  const me = useAppSelector(selectMe);
  const dispatch = useAppDispatch();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();
  const [value, setValue] = useState("");
  const [valueForCurrent, setValueForCurrent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Bound to display_name, not name: showing the inherited name here would
  // turn it into an explicit override the moment the user pressed Save.
  const current = me?.display_name ?? null;

  if (current !== valueForCurrent) {
    setValueForCurrent(current);
    setValue(current || "");
    setError(null);
  }

  const trimmed = value.trim();
  const changed = trimmed !== (current || "");
  const canSave = changed && !isLoading && trimmed.length <= MAX_DISPLAY_NAME_LENGTH;

  const handleSave = async () => {
    setError(null);
    try {
      const profile = await updateProfile({
        display_name: trimmed || null,
      }).unwrap();
      dispatch(setMeProfile(profile));
    } catch (e) {
      setError(getApiErrorDetail(e, "Could not save that name. Please try again."));
    }
  };

  // Only meaningful when a provider name exists to fall back to; otherwise
  // clearing the field leaves the nickname showing, which is not worth
  // promising in advance.
  const inherited = !current && me?.name;

  return (
    <div className="flex flex-col gap-2">
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel
          htmlFor="display-name"
          className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground"
        >
          Display name
        </FieldLabel>
        <Input
          id="display-name"
          type="text"
          value={value}
          maxLength={MAX_DISPLAY_NAME_LENGTH}
          placeholder={me?.name || "Your name"}
          onChange={(e) => setValue(e.target.value)}
          aria-invalid={error ? true : undefined}
          className="text-[13px]"
        />
        {!error && (
          <p className="text-[11px] text-muted-foreground">
            {inherited
              ? `Currently showing “${me?.name}” from your sign-in. Set a name here to override it.`
              : "Shown next to your records. Leave empty to use the name from your sign-in."}
          </p>
        )}
        {error && <FieldError errors={[{ message: error }]} />}
      </Field>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
